<template>
    <section class="bg-[#f7f4ef] text-[#171717]">
        <div class="mx-auto max-w-7xl px-5 lg:px-12">
            <div class="flex items-center justify-between gap-6">
                <h2 class="font-serif text-2xl uppercase">
                    {{ t.gallery.title }}
                </h2>

                <button
                    type="button"
                    class="hidden text-sm uppercase tracking-wide text-[#9b8565] transition hover:text-black md:block"
                    @click="openGallery(0)"
                >
                    {{ t.gallery.showAll }}
                </button>
            </div>

            <div class="mt-6 -mx-5 overflow-x-auto px-5 md:mx-0 md:px-0">
                <div class="flex w-max gap-2 md:w-auto md:flex-wrap">
                    <button
                        v-for="tab in tabs"
                        :key="tab.key"
                        type="button"
                        class="shrink-0 rounded-full border px-4 py-2 text-sm uppercase tracking-wide transition"
                        :class="activeTab === tab.key
                            ? 'border-black bg-black text-white'
                            : 'border-black/10 bg-white/50 text-black/60 hover:text-black'"
                        @click="setActiveTab(tab.key)"
                    >
                        {{ tab.title }}
                    </button>
                </div>
            </div>

            <div class="mt-7">
                <Swiper
                    v-if="filteredGalleryImages.length"
                    :key="activeTab"
                    :modules="swiperModules"
                    :slides-per-view="1.15"
                    :space-between="12"
                    :loop="false"
                    :speed="650"
                    :breakpoints="breakpoints"
                    class="gallery-swiper"
                    @swiper="onSwiper"
                    @slideChange="onSlideChange"
                    @progress="onProgress"
                >
                    <SwiperSlide
                        v-for="(image, index) in filteredGalleryImages"
                        :key="`${image.src}-${activeTab}-${index}`"
                    >
                        <button
                            type="button"
                            class="group block w-full overflow-hidden rounded-2xl bg-black"
                            @click="openGallery(index)"
                        >
                            <div class="relative h-56 md:h-60">
                                <img
                                    :src="image.src"
                                    :alt="image.alt"
                                    :loading="index < 2 ? 'eager' : 'lazy'"
                                    :fetchpriority="index < 2 ? 'high' : 'low'"
                                    decoding="async"
                                    class="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                                >

                                <div class="absolute inset-0 bg-black/0 transition duration-300 group-hover:bg-black/20"></div>

                                <div class="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/35 text-white opacity-0 backdrop-blur-md transition duration-300 group-hover:opacity-100">
                                    <IconZoom class="h-4 w-4"/>
                                </div>
                            </div>
                        </button>
                    </SwiperSlide>
                </Swiper>
            </div>

            <div class="mt-7 flex items-center gap-5">
                <div class="hidden items-center gap-3 md:flex">
                    <button
                        type="button"
                        class="flex h-14 w-14 items-center justify-center rounded-full border border-black/10 bg-black/[0.03] text-black transition hover:bg-black hover:text-white disabled:pointer-events-none disabled:opacity-30"
                        :disabled="isBeginning"
                        @click="slidePrev"
                    >
                        <IconAngleLeft class="h-5 w-5"/>
                    </button>

                    <button
                        type="button"
                        class="flex h-14 w-14 items-center justify-center rounded-full border border-black/10 bg-black/[0.03] text-black transition hover:bg-black hover:text-white disabled:pointer-events-none disabled:opacity-30"
                        :disabled="isEnd"
                        @click="slideNext"
                    >
                        <IconAngleLeft class="h-5 w-5 rotate-180"/>
                    </button>
                </div>

                <div class="flex w-full items-center gap-5">
                    <div class="h-px w-full overflow-hidden rounded-full bg-black/10 md:h-[3px]">
                        <div
                            class="h-full rounded-full bg-[#c8b79c] transition-all duration-300"
                            :style="{ width: progressWidth }"
                        ></div>
                    </div>

                    <div class="min-w-16 text-right font-serif text-2xl leading-none md:min-w-24 md:text-4xl">
                        {{ displayIndex }}/{{ filteredGalleryImages.length }}
                    </div>
                </div>
            </div>

            <button
                type="button"
                class="mt-6 flex h-12 w-full items-center justify-center rounded-xl border border-black/10 text-sm uppercase tracking-wide text-black/70 md:hidden"
                @click="openGallery(0)"
            >
                {{ t.gallery.showAll }}
            </button>
        </div>

        <VueEasyLightbox
            :visible="lightboxVisible"
            :imgs="lightboxImages"
            :index="lightboxIndex"
            :move-disabled="true"
            @hide="lightboxVisible = false"
        />
    </section>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode } from 'swiper/modules';

import VueEasyLightbox from 'vue-easy-lightbox';

import 'swiper/css';

import { useHomeStore } from '@js/store/homeStore.js';
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import IconZoom from "@js/components/Icons/IconZoom.vue";

export default {
    name: 'GallerySection',

    components: {
        IconZoom,
        IconAngleLeft,
        Swiper,
        SwiperSlide,
        VueEasyLightbox,
    },

    data() {
        return {
            swiperInstance: null,

            activeTab: 'all',

            lightboxVisible: false,
            lightboxIndex: 0,

            progress: 0,
            isBeginning: true,
            isEnd: false,

            swiperModules: [
                FreeMode,
            ],

            breakpoints: {
                640: {
                    slidesPerView: 2.2,
                    spaceBetween: 14,
                },
                1024: {
                    slidesPerView: 5,
                    spaceBetween: 14,
                },
            },
        };
    },

    computed: {
        homeStore() {
            return useHomeStore();
        },

        locale() {
            return this.homeStore.getLocale(this.$page);
        },

        t() {
            return this.homeStore.getTranslations(this.locale);
        },

        tabs() {
            return this.homeStore.getTabs(this.t);
        },

        galleryImages() {
            return this.homeStore.galleryImages || [];
        },

        filteredGalleryImages() {
            if (this.activeTab === 'all') {
                return this.galleryImages;
            }

            return this.galleryImages.filter((image) => image.group === this.activeTab);
        },

        displayIndex() {
            if (!this.filteredGalleryImages.length) {
                return 0;
            }

            return Math.min(
                this.filteredGalleryImages.length,
                Math.max(1, Math.round(this.progress * (this.filteredGalleryImages.length - 1)) + 1)
            );
        },

        progressWidth() {
            if (!this.filteredGalleryImages.length) {
                return '0%';
            }

            return `${(this.displayIndex / this.filteredGalleryImages.length) * 100}%`;
        },

        lightboxImages() {
            return this.filteredGalleryImages.map((image) => image.src);
        },
    },

    beforeUnmount() {
        if (this.swiperInstance && !this.swiperInstance.destroyed) {
            this.swiperInstance.destroy(true, false);
        }

        this.swiperInstance = null;
    },

    methods: {
        onSwiper(swiper) {
            this.swiperInstance = swiper;
            this.syncSwiperState(swiper);
        },

        onSlideChange(swiper) {
            this.syncSwiperState(swiper);
        },

        onProgress(swiper, progress) {
            this.progress = progress || 0;
            this.syncSwiperState(swiper);
        },

        syncSwiperState(swiper) {
            if (!swiper) {
                this.progress = 0;
                this.isBeginning = true;
                this.isEnd = false;
                return;
            }

            this.progress = swiper.progress || 0;
            this.isBeginning = Boolean(swiper.isBeginning);
            this.isEnd = Boolean(swiper.isEnd);
        },

        slidePrev() {
            if (!this.swiperInstance || this.swiperInstance.destroyed) {
                return;
            }

            this.swiperInstance.slidePrev();
        },

        slideNext() {
            if (!this.swiperInstance || this.swiperInstance.destroyed) {
                return;
            }

            this.swiperInstance.slideNext();
        },

        openGallery(index) {
            if (!this.filteredGalleryImages.length) {
                return;
            }

            this.lightboxIndex = index;
            this.lightboxVisible = false;

            this.$nextTick(() => {
                this.lightboxVisible = true;
            });
        },

        setActiveTab(tab) {
            this.activeTab = tab;
            this.progress = 0;
            this.isBeginning = true;
            this.isEnd = false;
            this.lightboxVisible = false;
            this.lightboxIndex = 0;

            this.$nextTick(() => {
                if (!this.swiperInstance || this.swiperInstance.destroyed) {
                    return;
                }

                this.swiperInstance.slideTo?.(0, 0);
                this.swiperInstance.update?.();
                this.syncSwiperState(this.swiperInstance);
            });
        },
    },
};
</script>

<style lang="scss">
.gallery-swiper {
    overflow: hidden;
}

.gallery-swiper .swiper-slide {
    height: auto;
}

.gallery-tabs-scroll {
    scrollbar-width: none;
}

.gallery-tabs-scroll::-webkit-scrollbar {
    display: none;
}
</style>
